import React, { useEffect, useState } from "react";
import { Mail, MapPin, Send, ShieldCheck, KeyRound, MessageCircle } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { submitContact } from "../api";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "General", message: "" });
  const [sending, setSending] = useState(false);
  const { toast } = useToast();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await submitContact(form);
      toast({
        title: "Message sent",
        description: "We'll reply from an encrypted address within 24 hours.",
      });
      setForm({ name: "", email: "", subject: "General", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Delivery failed. Try again.";
      toast({ title: "Couldn't send", description: String(msg) });
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="min-h-screen bg-black text-white pt-24">
      <section className="relative pb-10">
        <div className="absolute inset-0 aurora-purple opacity-70 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 py-14">
          <div className="chip">Contact</div>
          <h1 className="mt-4 font-headline text-5xl md:text-6xl font-bold leading-[0.95] text-gradient-purple">
            Get in touch.<br />Discreetly.
          </h1>
          <p className="mt-4 text-white/60 max-w-xl">
            Questions about a device, a tier or a threat model? Reach us on
            Telegram or encrypted email — we usually reply within 24 hours.
          </p>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-6">
          {/* Info */}
          <div className="space-y-4">
            <InfoCard icon={Mail} title="Encrypted email">
              <a href="mailto:contact@phantomphone.local" className="text-purple-300 hover:text-white">
                contact@phantomphone.local
              </a>
              <div className="text-xs text-white/40 mt-1">
                <a
                  href="/phantom-industries-pgp.asc"
                  download="phantom-industries-pgp.asc"
                  className="text-purple-300 hover:text-white underline underline-offset-4"
                  data-testid="contact-pgp-download"
                >
                  Download PGP public key (.asc)
                </a>
              </div>
            </InfoCard>
            <InfoCard icon={KeyRound} title="PGP encrypted mail">
              <div className="text-purple-300 break-all">phantom-industries-pgp.asc</div>
              <div className="text-xs text-white/40 mt-1">
                Preferred channel for sensitive matters. Download the key above
                and encrypt your message before sending.
              </div>
            </InfoCard>
            <InfoCard icon={MessageCircle} title="Telegram">
              <a
                href="https://t.me/Phantom0091"
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-300 hover:text-white transition-colors"
              >
                @Phantom0091
              </a>
              <div className="text-xs text-white/40 mt-1">Fastest response for general enquiries.</div>
            </InfoCard>
            <InfoCard icon={MapPin} title="Australia HQ">
              <div className="text-white/70">Melbourne, VIC</div>
              <div className="text-xs text-white/40 mt-1">By appointment only.</div>
            </InfoCard>
            <InfoCard icon={ShieldCheck} title="Response window">
              <div className="text-white/70">Within 24 hours</div>
              <div className="text-xs text-white/40 mt-1">Priority support for Phantom tier customers.</div>
            </InfoCard>
          </div>

          {/* Form */}
          <form onSubmit={submit} className="md:col-span-2 card-glass rounded-3xl p-8 space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Name">
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="input"
                  placeholder="How should we call you?"
                />
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="input"
                  placeholder="you@protonmail.com"
                />
              </Field>
            </div>
            <Field label="Subject">
              <select
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="input"
              >
                <option>General</option>
                <option>Device configuration</option>
                <option>Bulk / enterprise order</option>
                <option>Threat model consultation</option>
                <option>Warranty</option>
              </select>
            </Field>
            <Field label="Message">
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                rows={6}
                className="input resize-none"
                placeholder="Tell us what you need. No sensitive information in this form — use PGP-encrypted email for that."
              />
            </Field>
            <button type="submit" disabled={sending} className="btn-primary">
              {sending ? "Sending…" : (
                <>Send message <Send className="h-4 w-4" /></>
              )}
            </button>
            <div className="text-xs text-white/40">
              We reply from an encrypted address within 24 hours.
            </div>
          </form>
        </div>
      </section>

      <style>{`
        .input {
          width: 100%;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px;
          padding: 12px 14px;
          color: #fff;
          font-size: 14px;
          outline: none;
          transition: border-color 200ms ease, background 200ms ease;
        }
        .input:focus {
          border-color: rgba(168,85,247,0.6);
          background: rgba(168,85,247,0.05);
        }
      `}</style>
    </main>
  );
};

const InfoCard = ({ icon: Icon, title, children }) => (
  <div className="card-glass rounded-2xl p-5">
    <div className="flex items-center gap-3">
      <div className="h-9 w-9 rounded-lg bg-purple-500/10 border border-purple-500/30 flex items-center justify-center">
        <Icon className="h-4 w-4 text-purple-300" />
      </div>
      <div className="font-display font-semibold text-white">{title}</div>
    </div>
    <div className="mt-3 text-sm">{children}</div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">{label}</div>
    {children}
  </label>
);

export default Contact;
